import { css } from 'styled-components';
import { COMPONENT_SIZE } from 'constants';
import {
  pxToRem,
  pl,
  py,
  mr,
  ml,
  Colors,
} from '@maskott/tactileo.designsystem';
import { getCorrectThemeCss } from 'utils/themes.util';
import { zoom150Media } from 'constants/style';
import { getFontSizeToUse } from 'utils/common.util';

export const FOOTER_BOTTOM_HEIGHT = 56;
export const FOOTER_ZOOM_RESULT_HEIGHT = 120;

export const SIZE_SET = {
  [COMPONENT_SIZE.MEDIUM]: {
    HEIGHT: 88,
  },
  [COMPONENT_SIZE.SMALL]: {
    HEIGHT: 64,
  },
  [COMPONENT_SIZE.XSMALL]: {
    HEIGHT: 52,
  },
  [COMPONENT_SIZE.XXSMALL]: {
    HEIGHT: 40,
  },
};

export const FOOTER_TOP_HEIGHT = (size) => SIZE_SET[size]?.HEIGHT;

export const FooterWrapperStyle = css`
  display: flex;
  flex-direction: column;
  grid-area: footer;
  position: fixed;
  z-index: 2;
  bottom: 0;
  left: 0;
  right: 0;
  color: ${(p) =>
    Colors(p.theme.colors.neutral, p.theme.colors.lightness.light30, 1)};
  border-top: ${(p) =>
    p.$isOpenAccessibilityMenu
      ? `${pxToRem(2)} solid ${Colors(
          p.theme.colors.primary,
          p.theme.colors.lightness.light80,
          1,
        )}`
      : 'unset'};
`;

export const FooterTopWrapperStyle = css`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${(p) => pxToRem(SIZE_SET[p.size].HEIGHT)};
  background-color: ${(p) =>
    Colors(p.theme.colors.primary, p.theme.colors.lightness.light90, 1)};
  ${(p) => {
    if (p.mode && p.mode.dark) {
      return css`
        background-color: ${(p) =>
          Colors(
            p.theme.colors[p.mode.color],
            p.theme.colors.lightness.light30,
            1,
          )};
      `;
    }
  }}
`;

export const FooterBottomWrapperStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1;
  column-gap: ${pxToRem(32)};
  text-align: left;
  width: 100%;
  height: ${pxToRem(FOOTER_BOTTOM_HEIGHT)};
  ${py(1.5)};
  ${pl(3)};
  background: ${(p) =>
    Colors(p.theme.colors.primary, p.theme.colors.lightness.light100, 1)};
  color: ${(p) =>
    Colors(p.theme.colors.neutral, p.theme.colors.lightness.light30, 1)};
  box-shadow: 0 0 10px 1px ${(p) => Colors(p.theme.colors.primary, 0, 0.2)};
`;

export const InformationsWrapperStyle = css`
  display: flex;
`;

export const ButtonWrapperStyle = css`
  display: flex;
  flex-direction: row;
`;

export const LogoWrapperStyle = css`
  display: flex;
  align-items: center;
`;

export const LogoStyle = css`
  width: auto;
  height: 32px;
  max-width: ${pxToRem(84)}px;
  max-height: ${pxToRem(32)}px;
  object-fit: cover;
  
  ${(p) => {
      return getCorrectThemeCss(p.theme.name)?.logo;
  }}
`;

export const TitleWrapperStyle = css`
  display: flex;
  align-items: center;
  ${pl(4)};
`;

export const AlignWrapperStyle = css`
  display: flex;
  & > *:not(:last-child) {
    ${mr(2)};
  }

  .titleGroup{
    display: flex;
    span{
      margin: 0 0.5rem;
    }
  }

  ${(p) => {
      if (p.$isMobile || (p.$isRealTab && !p.$isLandscape)) {
        return css`
           @media ${zoom150Media} {
            flex-direction: column;
            align-items: flex-start;
            & > p,
            & > h1,
            & > h2 {
              -webkit-line-clamp: 2;
            }
          }
        `;
      }else{
        return css`
            align-items: center;
        `;
      }
    }};
`;

export const CollectionStyle = css`
  ${(p) => getFontSizeToUse('Title', 'Small', 'Bold', p.size)};
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
`;

export const TitleStyle = css`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  ${(p) => getFontSizeToUse('Title', 'Small', 'Regular', p.size)};
`;

export const TitleDescriptionStyle = css`
  align-self: flex-end;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  ${(p) => getFontSizeToUse('Title', 'Small', 'Regular', p.size)};
  @media ${zoom150Media} {
    align-self: flex-start;
  }
`;

export const ProgressWrapperStyle = css`
  display: flex;
  ${ml(4)};

  div[role='menu'] {
    right: 0;
  }
`;

export const ActiveButtonStyle = css`
  ${(p) => {
    if (p.active) {
      return `
        background-color: ${Colors(
          p.theme.colors.primary,
          p.theme.colors.lightness.light70,
          1,
        )};
      `;
    }
  }}
`;

export const FooterButtonStyle = css`
  top: 0;
  right: 0;
  height: auto;
  min-width: ${pxToRem(24)};
  ${py(0)};
`;

export const FooterResultWrapperStyle = css`
  width: 100vw;
  display: flex;
  justify-content: space-between;
  text-align: left;
  height: ${pxToRem(FOOTER_BOTTOM_HEIGHT)};
  background: white;
`;

export const ReturnWrapperStyle = css`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  height: 100%;
  width: ${pxToRem(72)};

  background-color: ${(p) =>
    Colors(p.theme.colors.primary, p.theme.colors.lightness.light70, 1)};
`;

export const ReturnIconStyle = css`
  color: currentColor;
  width: ${pxToRem(24)};
  height: ${pxToRem(24)};
`;

export const ResultLabelStyle = css`
  ${(p) => getFontSizeToUse('Body', 'Medium', 'Bold', p.size)};
  align-self: center;
  ${ml(2)};
`;
